import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { useBoardBackgroundEditor } from '@/hooks/useBoardBackgroundEditor';
import { boardService } from '@/lib/boardService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export const CreateBoardDialog: React.FC<{
  onBoardCreated?: () => void;

  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}> = ({ onBoardCreated, open: controlledOpen, onOpenChange, hideTrigger = false }) => {
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = React.useState(false);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange !== undefined ? onOpenChange : setInternalOpen;
  const [title, setTitle] = React.useState("Untitled Board");
  const [description, setDescription] = React.useState("Plan, prioritize, and track work visually. Create tasks, drag across states, and archive completed items with ease.");
  const [creating, setCreating] = React.useState(false);
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const {
    backgroundType,
    setBackgroundType,
    colorVal,
    setColorVal,
    gradFrom,
    gradTo,
    gradAngle,
    overlayOpacity,
    imagePreview,
    setOverlayOpacity,
    updateGradient,
    handleImageUpload,
    getBackgroundValue,
  } = useBoardBackgroundEditor({
    initialBackgroundType: 'gradient',
    initialColor: '#e2e8f0',
    initialGradFrom: '#8b5cf6',
    initialGradTo: '#06b6d4',
    initialGradAngle: 135,
    initialImagePreview: null,
    initialOverlayOpacity: 0,
  });

  const handleCreate = async () => {
    if (!currentWorkspace || !user) {
      toast.error('Please select a workspace first');
      return;
    }

    try {
      setCreating(true);
      const background = getBackgroundValue();

      const newBoard = await boardService.createBoard(
        currentWorkspace.id,
        user.id,
        title,
        description,
        background
      );

      setOpen(false);
      resetForm();
      onBoardCreated?.();
      navigate(`/task-board/${newBoard.slug}`);
      toast.success('Board created successfully');
    } catch (error) {
      console.error('Error creating board:', error);
      toast.error('Failed to create board');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setTitle("Untitled Board");
    setDescription("Plan, prioritize, and track work visually. Create tasks, drag across states, and archive completed items with ease.");
    setBackgroundType("gradient");
    setColorVal("#e2e8f0");
    updateGradient("#8b5cf6", "#06b6d4", 135);
    setOverlayOpacity(0);
  };

  const contentCls = 'bg-board-column border-border text-card-foreground';
  const labelCls = 'text-foreground';
  const inputCls = 'bg-background border-input text-foreground placeholder:text-muted-foreground';
  const selectTriggerCls = inputCls;
  const rangeCls = 'accent-workspace-accent bg-muted rounded h-2';
  const primaryBtn = 'bg-workspace-accent text-white hover:bg-workspace-accent-hover';
  const cancelBtnCls = '';

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) resetForm();
    }}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button className="bg-workspace-accent text-white hover:bg-workspace-accent-hover">
            <Plus className="mr-2 h-4 w-4" /> New Board
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto border ${contentCls}`}>
        <DialogDescription className="sr-only">Create a new board</DialogDescription>
        <DialogHeader>
          <DialogTitle className="text-foreground">Create New Board</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label className={labelCls} htmlFor="board-title">Board Name</Label>
            <Input 
              id="board-title" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder="Enter board name"
              className={inputCls}
            />
          </div>
          <div className="grid gap-2">
            <Label className={labelCls} htmlFor="board-desc">Description</Label>
            <Textarea 
              id="board-desc" 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              placeholder="Enter board description"
              className={inputCls}
            />
          </div>
          <div className="grid gap-2">
            <Label className={labelCls}>Background Type</Label>
            <Select
              value={backgroundType}
              onValueChange={(val: "gradient" | "color" | "image") => setBackgroundType(val)}
            >
              <SelectTrigger className={selectTriggerCls}>
                <SelectValue placeholder="Select background type" />
              </SelectTrigger>
              <SelectContent className={contentCls}>
                <SelectItem value="gradient">Gradient</SelectItem>
                <SelectItem value="color">Solid Color</SelectItem>
                <SelectItem value="image">Image Upload</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            {backgroundType === "color" && (
              <>
                <Label className={labelCls} htmlFor="board-color">Pick Color</Label>
                <div className="flex items-center gap-3">
                  <Input 
                    id="board-color" 
                    type="color" 
                    value={colorVal} 
                    onChange={(e) => setColorVal(e.target.value)} 
                    className="h-10 w-16 p-0 border-0 rounded-md ring-0 outline-none bg-transparent" 
                  />
                  <Input 
                    value={colorVal} 
                    onChange={(e) => setColorVal(e.target.value)} 
                    aria-label="Color hex" 
                    className={inputCls}
                  />
                </div>
              </>
            )}
            {backgroundType === "gradient" && (
              <>
                <Label className={labelCls}>Gradient</Label>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">From</span>
                    <Input 
                      type="color" 
                      value={gradFrom} 
                      onChange={(e) => updateGradient(e.target.value, gradTo, gradAngle)} 
                      className="h-10 w-16 p-0 border-0 rounded-md ring-0 outline-none bg-transparent" 
                    />
                    <Input 
                      value={gradFrom} 
                      onChange={(e) => updateGradient(e.target.value, gradTo, gradAngle)} 
                      aria-label="From color" 
                      className={inputCls}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">To</span>
                    <Input 
                      type="color" 
                      value={gradTo} 
                      onChange={(e) => updateGradient(gradFrom, e.target.value, gradAngle)} 
                      className="h-10 w-16 p-0 border-0 rounded-md ring-0 outline-none bg-transparent" 
                    />
                    <Input 
                      value={gradTo} 
                      onChange={(e) => updateGradient(gradFrom, e.target.value, gradAngle)} 
                      aria-label="To color" 
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <Label className={labelCls} htmlFor="board-angle">Angle</Label>
                  <div className="flex items-center gap-3">
                    <Input 
                      id="board-angle" 
                      type="range" 
                      min={0} 
                      max={360} 
                      value={gradAngle} 
                      onChange={(e) => updateGradient(gradFrom, gradTo, Number(e.target.value))} 
                      className={rangeCls}
                    />
                    <Input 
                      type="number" 
                      className={`w-24 ${inputCls}`} 
                      value={gradAngle} 
                      onChange={(e) => updateGradient(gradFrom, gradTo, Number(e.target.value))} 
                      aria-label="Gradient angle"
                    />
                  </div>
                </div>
                <div 
                  className="mt-3 h-16 rounded-md border border-border" 
                  style={{ backgroundImage: `linear-gradient(${gradAngle}deg, ${gradFrom}, ${gradTo})` }} 
                  aria-label="Gradient preview" 
                />
              </>
            )}
            {backgroundType === "image" && (
              <>
                <Label className={labelCls} htmlFor="board-image">Upload Image</Label>
                <Input 
                  id="board-image" 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => handleImageUpload(e.target.files?.[0] ?? null)} 
                  className={inputCls}
                />
                {imagePreview && (
                  <>
                    <div className="mt-2 rounded-md max-h-40 overflow-hidden relative">
                      <img src={imagePreview} alt="Board background preview" className="object-cover w-full h-full" />
                      {overlayOpacity > 0 && (
                        <div 
                          className="absolute inset-0 bg-black" 
                          style={{ opacity: overlayOpacity / 100 }}
                        />
                      )}
                    </div>
                    <div className="mt-3">
                      <Label className={labelCls} htmlFor="overlay-opacity">Overlay Opacity (%)</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <Input 
                          id="overlay-opacity"
                          type="range" 
                          min={0} 
                          max={80} 
                          value={overlayOpacity} 
                          onChange={(e) => setOverlayOpacity(Number(e.target.value))} 
                          className={rangeCls}
                        />
                        <Input 
                          type="number" 
                          className={`w-20 ${inputCls}`} 
                          min={0}
                          max={80}
                          value={overlayOpacity} 
                          onChange={(e) => setOverlayOpacity(Number(e.target.value))} 
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className={primaryBtn} onClick={handleCreate} disabled={creating}>Create Board</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default CreateBoardDialog;
